/**
 * @ngdoc directive
 * @name runner.library.tabs.procedures
 * @module flowingly.runner.library
 * @description  This component is the SOPs tab of the Library component
 * @usage
 */

import { SharedAngular } from '@Client/@types/sharedAngular';
import IProcedureOverview from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureOverview';
import { DeleteEntityTypes } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { IFilterDate, IFilterService, IScope } from 'angular';
import { IStateService } from 'angular-ui-router';

declare interface ILibraryProcedureOverview extends IProcedureOverview {
  updatedDateLocal: string;
  updatedDateTimeLocal: string;
}
declare const moment: Moment;

angular
  .module('flowingly.runner.library')
  .component('flowLibraryTabsProcedures', {
    templateUrl:
      'Client/runner.library/runner.library.procedures/runner.library.tabs.procedures.tmpl.html',
    controller: [
      '$state',
      '$filter',
      '$scope',
      'procedureApiService',
      'dialogService',
      'notificationService',
      function (
        $state: IStateService,
        $filter: IFilterService,
        $scope: IScope,
        procedureApiService: SharedAngular.ProcedureApiService,
        dialogService: SharedAngular.DialogService,
        notificationService: SharedAngular.NotificationService
      ) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const $ctrl: {
          procedures: ILibraryProcedureOverview[];
          [key: string]: unknown;
        } = this;
        $ctrl.procedures = [];
        $ctrl.uniqueCreatorNames = [];
        $ctrl.options = {
          dataSource: {
            data: $ctrl.procedures,
            pageSize: 15,
            schema: {
              model: {
                fields: {
                  updatedDateLocal: { type: 'date' }
                }
              }
            },
            sort: {
              field: 'updatedDateLocal',
              dir: 'desc',
              compare: dateTimeCompare
            }
          },
          noRecords: {
            template: '<flow-library-no-results></flow-library-no-results>'
          },
          scrollable: true,
          pageable: true,
          sortable: true,
          resizable: true,
          filterable: {
            mode: 'row',
            operators: {
              date: {
                gte: 'Is after or equal to',
                lte: 'Is before or equal to'
              }
            }
          },
          columns: getColumns()
        };

        procedureApiService.getProcedures().then((results) => {
          $ctrl.procedures = results as ILibraryProcedureOverview[];
          $ctrl.uniqueCreatorNames = [
            ...new Set(
              $ctrl.procedures.map((procedure) => procedure.creatorName)
            )
          ];

          for (const procedure of $ctrl.procedures) {
            procedure.updatedDateLocal = $filter<IFilterDate>('utcToLocal')(
              procedure.updatedDate,
              'yyyy-MM-dd'
            );
            procedure.updatedDateTimeLocal = $filter<IFilterDate>('utcToLocal')(
              procedure.updatedDate,
              'yyyy-MM-dd HH:mm:ss'
            );
          }

          $ctrl.noRows = !$ctrl.procedures || $ctrl.procedures.length === 0;
          $ctrl.gridInitDone = true;
          $ctrl.doGridResize = true;
          $scope.libraryGrid.dataSource.data($ctrl.procedures);
          $scope.libraryGrid.refresh();
        });

        function getColumns(): kendo.ui.GridColumn[] {
          return [
            {
              field: 'name',
              title: 'Name',
              filterable: {
                cell: {
                  operator: 'contains',
                  suggestionOperator: 'contains',
                  showOperators: false,
                  template: function (args) {
                    args.element.kendoAutoComplete({
                      dataSource: new kendo.data.DataSource({
                        data: $ctrl.procedures
                      }),
                      dataTextField: 'name',
                      placeholder: 'Search by Name',
                      valuePrimitive: true,
                      filter: 'contains',
                      autoWidth: true
                    });
                  }
                }
              }
            },
            {
              field: 'creatorName',
              title: 'Created By',
              filterable: {
                cell: {
                  operator: 'contains',
                  showOperators: false,
                  template: function (args) {
                    args.element.kendoComboBox({
                      dataSource: $ctrl.uniqueCreatorNames,
                      placeholder: 'Search by creator',
                      valuePrimitive: true,
                      autoWidth: true
                    });
                  }
                }
              }
            },
            {
              field: 'updatedDateLocal',
              title: 'Last Updated',
              sortable: {
                compare: dateTimeCompare
              },
              template: (procedure: ILibraryProcedureOverview) => {
                return (
                  '<span title="' +
                  procedure.updatedDateTimeLocal +
                  '">' +
                  moment(procedure.updatedDateLocal).format('DD/MM/YYYY') +
                  '</span>'
                );
              },
              filterable: {
                cell: {
                  template: function (args) {
                    args.element
                      .kendoDatePicker({
                        placeholder: 'Search by last updated date',
                        format: 'dd/MM/yyyy'
                      })
                      .attr('placeholder', 'Search by last updated date');
                  }
                }
              }
            },
            {
              field: 'formCount',
              title: 'Forms',
              filterable: false,
              width: 200,
              headerTemplate: () => {
                return '<span title="The number of Step forms this SOP is included in">Forms</span>';
              }
            },
            {
              field: 'viewCount',
              title: 'Views',
              filterable: false,
              width: 200,
              headerTemplate: () => {
                return '<span title="The number of times this SOP has been viewed in a Step form">Views</span>';
              }
            },
            {
              title: 'Actions',
              width: '80px',
              filterable: false,
              template:
                '<span class="list-actions">' +
                '<span ng-click="$ctrl.editProcess(dataItem)" class="list-actions__action">' +
                '<i class="fa-light fa-pencil" title="Edit"></i></span>' +
                '<span ng-click="$ctrl.deleteProcess(dataItem)" class="list-actions__action">' +
                '<i class="fa-light fa-trash-can" title="Delete"></i></span>' +
                '</span>'
            }
          ];
        }

        function dateTimeCompare(
          a: ILibraryProcedureOverview,
          b: ILibraryProcedureOverview
        ) {
          if (a.updatedDateTimeLocal < b.updatedDateTimeLocal) {
            return -1;
          }
          if (a.updatedDateTimeLocal > b.updatedDateTimeLocal) {
            return 1;
          }
          return 0;
        }

        this.editProcess = (procedure: IProcedureOverview) => {
          $state.go('app.runner.procedure', { procedureId: procedure.id }, {});
        };

        this.deleteProcess = (procedure: IProcedureOverview) => {
          dialogService
            .showDialog({
              template:
                'Client/runner.setup/runner.setup.delete/runner.setup.delete.dialog.tmpl.html',
              controller: 'setupDeleteDialogController',
              appendClassName: 'ngdialog-normal',
              scope: $scope,
              data: {
                itemToDelete: procedure,
                entityType: DeleteEntityTypes.procedure
              }
            })
            .then((result) => {
              if (dialogService.isCloseModalWithCancelAction(result)) {
                //user closed modal by clicking on overlay (or cancel or press Esc key)
                return;
              }

              procedureApiService
                .deleteProcedure(procedure.id)
                .then(() => {
                  notificationService.showSuccessToast('SOP Deleted');
                  $ctrl.procedures = [
                    ...$ctrl.procedures.filter((p) => p.id !== procedure.id)
                  ];
                  $ctrl.noRows = $ctrl.procedures.length === 0;
                  $scope.libraryGrid.dataSource.data($ctrl.procedures);
                  $scope.libraryGrid.refresh();
                  $ctrl.doGridResize = true;
                })
                .catch(() =>
                  notificationService.showErrorToast('Failed to delete SOP')
                );
            });
        };
      }
    ]
  });
